import {
  OperationVariables,
  QueryResult,
  TypedDocumentNode,
  WatchQueryFetchPolicy,
} from '@apollo/client';
import { captureMessage } from '@sentry/react';
import { ComponentType } from 'react';
import {
  LoaderFunction,
  Params,
  useLoaderData,
  useParams,
} from 'react-router-dom';

import { apolloClient } from 'contexts/graphql/Provider';
import useQuery from 'hooks/graphql/useQuery';
import { DeepPartial } from 'types';

import shouldRevalidateFn from './shouldRevalidateFn';

/* #__NO_SIDE_EFFECTS__ */
type Props<TData, TVariables> = {
  query: TypedDocumentNode<TData, TVariables>;
  getVariables?: (params: Params) => DeepPartial<TVariables>;
  skipTransition?: boolean;
};
export const withRouteQuery = <P, TData, TVariables extends OperationVariables>(
  Component: ComponentType<P & { queryResult: QueryResult<TData, TVariables> }>,
  { query, getVariables, skipTransition }: Props<TData, TVariables>
): ComponentType<P> & { loader: LoaderFunction } => {
  const ComponentWithRouteQuery = (props: P) => {
    const params = useParams();
    const { nextFetchPolicy } = useLoaderData() as {
      nextFetchPolicy: WatchQueryFetchPolicy;
    };
    const queryResult = useQuery(query, {
      variables: getVariables?.(params) as TVariables,
      fetchPolicy: nextFetchPolicy,
    });
    return <Component {...props} queryResult={queryResult} />;
  };

  // https://reactrouter.com/en/main/route/loader
  /* As we're outside of react rendering context we use the Apollo Client
   * directly instead of useQuery. This primes apollo cache, but does not
   * subscribe to store changes, which is why we still need the useQuery in the component
   * (with fetchPolicy: 'cache-only' to avoid duplicated first requests)
   */
  const loader: LoaderFunction<any> = async ({ params }) => {
    if (!apolloClient.current) {
      captureMessage('Apollo client is not initialized');
      return {};
    }
    const client = apolloClient.current;

    const cachedData = client.readQuery({
      query,
      variables: getVariables?.(params) as TVariables,
    });

    /** no need to execute the read in the cache again if we have cachedData */
    const getResult = async () =>
      cachedData ||
      client.query({
        query,
        variables: getVariables?.(params) as TVariables,
      });

    // Either wait for the query to finish to display the component because you need the data to render smooth animation,
    // or you want to display the component as soon as possible to display skeletons instead
    // If there is cachedData, we can safely wait for the Promise to resolve
    if (skipTransition && !cachedData) {
      getResult();
    } else {
      await getResult();
    }
    return {
      nextFetchPolicy: cachedData ? 'cache-and-network' : 'cache-only',
    };
  };

  return Object.assign(ComponentWithRouteQuery, {
    loader,
    shouldRevalidate: shouldRevalidateFn(getVariables),
  });
};
